import React, { useState, useEffect } from 'react';
import { Ticket, Plus, CheckCircle, XCircle } from 'lucide-react';
import { getCoupons, createCoupon, toggleCoupon } from '../services/api';

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [code, setCode] = useState('');
  const [extraCredits, setExtraCredits] = useState(5);
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { setCoupons((await getCoupons()) || []); }
    catch { setErr('Failed to load coupons.'); }
  };
  useEffect(() => { reload(); }, []);

  const doCreate = async () => {
    setErr(''); setMsg('');
    if (!code.trim()) { setErr('Enter a coupon code.'); return; }
    setBusy(true);
    try {
      await createCoupon({
        code: code.trim(),
        extra_credits: Number(extraCredits),
        max_redemptions: maxRedemptions ? Number(maxRedemptions) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        note,
      });
      setMsg(`Coupon ${code.trim().toUpperCase()} created.`);
      setCode(''); setExtraCredits(5); setMaxRedemptions(''); setExpiresAt(''); setNote('');
      reload();
    } catch (e) { setErr(e.response?.data?.detail || 'Failed to create coupon.'); }
    finally { setBusy(false); }
  };

  const doToggle = async (c) => {
    setErr(''); setMsg('');
    try { await toggleCoupon(c.code, !c.active); reload(); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to update coupon.'); }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
          <Ticket className="w-5 h-5 text-brand-400" /> Coupon Codes
        </h2>
        <p className="text-sm text-ink-300 mt-1">
          Reusable codes that grant self-serve students bonus practice-interview credits.
          Each code can be redeemed by a given student only once.
        </p>
      </div>

      {err && <div className="text-sm text-red-400">{err}</div>}
      {msg && <div className="text-sm text-green-400 flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> {msg}</div>}

      <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
        <p className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-brand-400" /> Create a coupon</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-ink-400 mb-1">Code</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="WELCOME10"
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100 uppercase" />
          </div>
          <div>
            <label className="block text-xs text-ink-400 mb-1">Extra credits per redemption</label>
            <input type="number" min={1} value={extraCredits} onChange={(e) => setExtraCredits(e.target.value)}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          </div>
          <div>
            <label className="block text-xs text-ink-400 mb-1">Max redemptions (blank = unlimited)</label>
            <input type="number" min={1} value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          </div>
          <div>
            <label className="block text-xs text-ink-400 mb-1">Expires (blank = never)</label>
            <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-ink-400 mb-1">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. college partnership batch"
              className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
          </div>
        </div>
        <button onClick={doCreate} disabled={busy}
          className="mt-3 px-5 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
          {busy ? 'Creating…' : 'Create coupon'}
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink-100 mb-3">Existing coupons</h3>
        {coupons.length === 0 ? (
          <p className="text-ink-400 text-sm py-6 text-center bg-ink-800 border border-ink-700 rounded-lg">No coupons yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-800">
              <thead className="bg-ink-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Credits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Redemptions</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-ink-900 divide-y divide-ink-800">
                {coupons.map((c) => (
                  <tr key={c.code} className="hover:bg-ink-800">
                    <td className="px-4 py-3 text-sm text-ink-100 font-mono">{c.code}</td>
                    <td className="px-4 py-3 text-sm text-ink-300">+{c.extra_credits}</td>
                    <td className="px-4 py-3 text-sm text-ink-400">{c.redemptions_count}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}</td>
                    <td className="px-4 py-3 text-sm text-ink-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.active ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'
                      }`}>{c.active ? 'Active' : 'Disabled'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => doToggle(c)} className="text-xs text-ink-300 hover:text-ink-100 flex items-center gap-1">
                        {c.active ? <><XCircle className="w-3.5 h-3.5" /> Disable</> : <><CheckCircle className="w-3.5 h-3.5" /> Enable</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Coupons;
